<template>
  <div class="pdf-render-annotation" aria-busy="isLoading">
    <!-- Smooth Fade Transition for the PDF Loader -->
    <transition name="fade">
      <div 
        v-if="isLoading" 
        class="pdf-loading-overlay" 
        role="alert" 
        aria-live="assertive"
      >
        <div class="pdf-loader">
          <div class="pdf-spinner"></div>
          <div class="pdf-loading-text">Loading PDF...</div>
        </div>
      </div>
    </transition>

    <!-- PDF Viewer: Keyed to force clean recreation on file swaps -->
    <pdf-annotate
      v-if="isReady"
      ref="pdfViewer"
      :key="renderedKey"
      :src="PDFurl"
      :toolbar="toolbar"
      @document-loaded="onPdfLoaded"
    />
  </div>
</template>

<script>
import 'pdf-annotate-vue/src/css/toolbar.css';
import 'pdf-annotate-vue/src/css/pdf_viewer.css';

import axios from 'axios';
import PdfAnnotate from 'pdf-annotate-vue';
import { bus } from '@/main';
import root from '@/_helpers/roots';
import toast from '@/services/toast';

// Safe ES5 fallback evaluation
const SAVE_ANNOTATION_URL = (root && root.saveAnnotationApiUrl && root.saveAnnotationApiUrl.link) || '';
const BUS_EVENT_NAME = 'appliedAnnotationChangesEmit';

export default {
  name: 'PdfRenderAnnotation',

  components: {
    PdfAnnotate
  },

  props: {
    PDFurl: {
      type: String,
      required: true,
      validator: function(value) {
        return value.indexOf('http') === 0 || value.indexOf('/') === 0 || value === '';
      }
    },
    pdfId: {
      type: [String, Number],
      required: true
    }
  },

  data() {
    return {
      isReady: false,
      isLoading: true,
      toolbar: true,
      renderFrameId: null
    };
  },

  computed: {
    renderedKey() {
      return this.pdfId + '-' + this.PDFurl;
    },
    storageKey() {
      return this.PDFurl + '/annotations';
    }
  },

  watch: {
    renderedKey: {
      handler: 'loadSavedAnnotations',
      immediate: true
    }
  },

  mounted() {
    bus.$on(BUS_EVENT_NAME, this.saveAnnotations);
  },

  beforeDestroy() {
    this.cancelActiveRenderLoops();
    bus.$off(BUS_EVENT_NAME, this.saveAnnotations);
  },

  methods: {
    cancelActiveRenderLoops() {
      if (this.renderFrameId) {
        cancelAnimationFrame(this.renderFrameId);
        this.renderFrameId = null;
      }
    },

    async loadSavedAnnotations() {
      if (!this.PDFurl || !this.pdfId) return;

      this.cancelActiveRenderLoops();
      this.isReady = false;
      this.isLoading = true;

      try {
        const res = await axios.get(SAVE_ANNOTATION_URL, {
          params: { paper_id: this.pdfId },
          timeout: 15000 
        });

        // Safe evaluation matrix replacing optional chaining
        const savedAnnotations = res && res.data && res.data.data && res.data.data.result && res.data.data.result.annotation_data;

        if (savedAnnotations) {
          localStorage.setItem(this.storageKey, savedAnnotations);
        } else {
          localStorage.removeItem(this.storageKey);
        }
      } catch (err) {
        console.error('[PDF Annotation Engine] Remote sync initialization failed:', err);
        localStorage.removeItem(this.storageKey); 
      } finally {
        this.$nextTick(() => {
          this.isReady = true;
          this.waitForPdfRender();
        });
      }
    },

    waitForPdfRender() {
      let attempts = 0;
      const maxAttempts = 150; 

      const checkPdf = () => {
        attempts++;
        
        // Removed ?. and added a safe element reference check
        const pdfPages = this.$el ? this.$el.querySelectorAll('.page') : null;

        if (pdfPages && pdfPages.length > 0) {
          this.isLoading = false;
          this.renderFrameId = null;
          return;
        }

        if (attempts < maxAttempts) {
          this.renderFrameId = requestAnimationFrame(checkPdf);
        } else {
          this.isLoading = false;
          this.renderFrameId = null;
          console.warn('[PDF Annotation Engine] Layout timeout reached.');
        }
      };

      this.renderFrameId = requestAnimationFrame(checkPdf);
    },

    onPdfLoaded() {
      this.isLoading = false;
    },

    async saveAnnotations() {
      if (!this.isReady) return;

      const viewerInstance = this.$refs.pdfViewer;
      if (viewerInstance && typeof viewerInstance.saveState === 'function') {
        viewerInstance.saveState();
      }

      const annotations = localStorage.getItem(this.storageKey) || '';
      
      const requestBody = {
        paper_id: this.pdfId,
        annotation_data: annotations
      };

      try {
        await axios.post(SAVE_ANNOTATION_URL, requestBody, { timeout: 10000 });
        toast.success('Annotations saved successfully');
      } catch (err) {
        // Safe evaluation matrix replacing optional chaining
        const errorMsg = (err && err.response && err.response.data && (err.response.data.user_msg || err.response.data.msg)) || 
                         'Failed to save annotations';
        toast.error(errorMsg);
      }
    }
  }
};
</script>

<style scoped lang="scss">
/* =========================================================
   1. PDF CONTAINER & TRANSITIONS
   ========================================================= */
.pdf-render-annotation {
  position: relative;
  width: 100%;
  min-height: 100dvh;
}

/* Vue Transition Classes */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease, backdrop-filter 0.4s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
  backdrop-filter: blur(0px);
}

/* =========================================================
   2. ENHANCED LOADER UI
   ========================================================= */
.pdf-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(38, 40, 42, 0.75);
  backdrop-filter: blur(5px);
}

.pdf-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 170px;
  padding: 32px;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.25);
}

.pdf-spinner {
  width: 42px;
  height: 42px;
  border: 4px solid #f3f4f6;
  border-top-color: #ff8a23;
  border-radius: 50%;
  animation: pdf-spin 0.8s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.pdf-loading-text {
  margin-top: 16px;
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  letter-spacing: 0.3px;
}

@keyframes pdf-spin {
  to { transform: rotate(360deg); }
}

/* =========================================================
   3. PDF VIEWER & TOOLBAR OVERRIDES
   ========================================================= */
::v-deep {
  #content-wrapper {
    right: 0 !important;
    &::-webkit-scrollbar {
        width: 0px;
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background-color: transparent;
    }

    .page {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
      margin: 1px auto -8px auto;
      position: relative;
      overflow: visible;
      border: none;
      background-clip: content-box;

      /* Annotation Fixes */
      .annotationLayer {
        opacity: 0.85;

        path[data-pdf-annotate-type="drawing"] {
          stroke: #ffca00 !important;
          fill: none !important;
          stroke-linecap: round;
          stroke-linejoin: round;
        }

        rect[data-pdf-annotate-type="area"] {
          stroke: #ff4d4f !important;
          stroke-width: 2 !important;
          fill: transparent !important;
        }

        text {
          fill: #1900ff !important;
          &[font-size="6.666666666666666"] {
            font-size: 11px !important;
          }
        }
      }
    }
  }

  /* --- FLOATING PILL TOOLBAR --- */
  .toolbar {
    position: fixed;
    top: 5px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10000;
    
    display: flex;
    align-items: center;
    gap: 8px;

    width: max-content !important;
    height: auto;
    padding: 6px 16px;

    background: rgba(30, 32, 34, 0.92);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 50px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
    backdrop-filter: blur(12px);

    @media only screen and (max-width: 767px) {
      display: none !important;
    }

    /* 1. HIDE UNUSED ITEMS */
    .cursor, .spacer, .hotspot-color, .highlight, .text-color,
    .arrow, .rotate-ccw, .pen-color, .rotate-cw, .strikeout, .comment,
    button[data-tooltype="emptycircle"], button[data-tooltype="circle"],
    button[data-tooltype="fillcircle"], button[data-tooltype="point"], .point {
      display: none !important;
    }

    /* 2. REORDER VISIBLE ITEMS */
    .pen { order: 1; }
    .pen-size { order: 2; } 
    .text { order: 3; }
    .text-size { order: 4; }
    .rectangle { order: 5; }
    .eraser { order: 6; }
    .scale { order: 7; }
    .clear { order: 8; }

    /* 3. TOOL BUTTONS */
    button, .pen, .text, .rectangle, .eraser, .clear {
      position: relative;
      z-index: 10; /* Keep buttons strictly below dropdowns */
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 36px;
      height: 36px;
      padding: 0;
      background: transparent;
      border: none;
      border-radius: 50%;
      color: #9ca3af !important;
      font-size: 18px;
      cursor: pointer;
      transition: all 0.2s ease;

      /* Force inner SVGs to inherit standard text color instead of hardcoded red */
      svg, path, rect, line {
        stroke: currentColor !important;
      }

      &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: #f3f4f6 !important;
      }

      &.active {
        background: #ff8a23 !important;
        color: #ffffff !important;
        box-shadow: 0 2px 8px rgba(255, 138, 35, 0.4);
      }
    }

    /* Fix Rectangle Specific Alignment */
    .rectangle {
      
      i, span, svg, div {
        margin: auto !important;
        display: block !important;
      }
      &:after{
          width: 15px;
          height: 15px;
          border: 1px solid currentColor;
          position: absolute;
          top: 10px;
          left: 10px;
      }
    }

    /* Clear Button - Matches toolbar until hovered */
    .clear {
      font-size: 24px !important;
      line-height: 1 !important;
      
      &:hover {
        background: rgba(239, 68, 68, 0.15) !important;
        color: #f87171 !important;
      }
    }

    /* 4. SELECT DROPDOWNS (Z-Index Fixes) */
    .pen-size, .text-size, .scale, select {
      position: relative;
      z-index: 50 !important; /* Forces dropdowns above active buttons */
      pointer-events: auto !important; 
    }

    select {
      height: 34px;
      min-width: 80px;
      padding: 0 24px 0 12px;
      background: rgba(0, 0, 0, 0.2);
      color: #e5e7eb !important;
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 20px;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      outline: none;
      transition: all 0.2s ease;

      &:hover, &:focus {
        border-color: #ff8a23;
      }
      
      option {
        background-color: #2b2d30 !important;
        color: #ffffff !important;
        font-size: 14px;
      }
    }

    /* Distinct Zoom (Scale) Dropdown Style */
    .scale {
      display: inline-flex;
      align-items: center;
      
      option:nth-child(3) { display: none !important; }

      select {
        background: transparent;
        border-color: transparent;
        color: #9ca3af !important;
        padding: 0 16px 0 8px;
        min-width: 70px;
        font-weight: 600;

        &:hover, &:focus {
          background: rgba(255, 255, 255, 0.08);
          border-color: transparent;
          color: #ffffff !important;
        }
      }
    }

    a {
      color: #e5e7eb;
      text-decoration: none;
    }
  }
}

/* =========================================================
   4. MOBILE FALLBACKS
   ========================================================= */
@media only screen and (max-width: 767px) {
  .pdf-render-annotation {
    min-height: 100vh;
  }
  .pdf-loading-overlay {
    position: fixed;
  }
}
</style>